import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { FaNotesMedical } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { ChipListSelect, FormSelect, FormTextField } from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import TemplateListPage, { statusParam } from "../../../components/organisms/TemplateListPage";
import TemplateEditorDrawer from "../../../components/organisms/TemplateEditorDrawer";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { fullDateTime, relativePast } from "../../Secretary/PatientsPage/patientListFormat";

import {
  useCreateDoctorDiagnosisTemplateMutation,
  useListDoctorDiagnosisTemplatesMutation,
  useSetDoctorDiagnosisTemplateStatusMutation,
  useUpdateDoctorDiagnosisTemplateMutation,
} from "../../../store/doctorApi";
import {
  useListDiagnosisCategoriesMutation,
  useListDiagnosesMutation,
} from "../../../store/systemVariablesApi";

export default function DoctorDiseaseTemplates() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", items: [] });

  const [diagnosisCategories, setDiagnosisCategories] = useState([]);
  const [diagnosisOptions, setDiagnosisOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const bootRef = useRef(false);
  const [listDoctorDiagnosisTemplates] = useListDoctorDiagnosisTemplatesMutation();
  const [createDoctorDiagnosisTemplate, { isLoading: isCreateLoading }] =
    useCreateDoctorDiagnosisTemplateMutation();
  const [updateDoctorDiagnosisTemplate, { isLoading: isUpdateLoading }] =
    useUpdateDoctorDiagnosisTemplateMutation();
  const [setDoctorDiagnosisTemplateStatus] =
    useSetDoctorDiagnosisTemplateStatusMutation();
  const [listDiagnosisCategories] = useListDiagnosisCategoriesMutation();
  const [listDiagnoses] = useListDiagnosesMutation();

  const isSaving = isCreateLoading || isUpdateLoading;

  const fetchList = async () => {
    if (!token) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await listDoctorDiagnosisTemplates({
        token,
        query,
        status: statusParam(status),
        page: page + 1,
        limit: rowsPerPage,
      }).unwrap();
      setItems(res?.data?.templates || []);
      setTotal(res?.data?.pagination?.total || 0);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Şablonlar alınamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDiagnosisCategories = async () => {
    if (!token) return;
    try {
      const res = await listDiagnosisCategories({
        token,
        status: "active",
        limit: 200,
      }).unwrap();
      setDiagnosisCategories(res?.data?.categories || []);
    } catch (e) {
      setDiagnosisCategories([]);
    }
  };

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    fetchList();
    fetchDiagnosisCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, rowsPerPage]);

  useEffect(() => {
    if (!token || !selectedCategory?._id) {
      setDiagnosisOptions([]);
      return;
    }
    const load = async () => {
      try {
        const res = await listDiagnoses({
          token,
          categoryId: selectedCategory._id,
          status: "active",
          page: 1,
          limit: 200,
        }).unwrap();
        setDiagnosisOptions(res?.data?.diagnoses || []);
      } catch (_) {
        setDiagnosisOptions([]);
      }
    };
    load();
  }, [token, selectedCategory, listDiagnoses]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", items: [] });
    setSelectedCategory(null);
    setOpen(true);
  };

  const openEdit = (template) => {
    setEditing(template);
    setForm({ name: template.name || "", items: template.items || [] });
    setSelectedCategory(null);
    setOpen(true);
  };

  // ChipListSelect: şablon öğesi `{ diagnosisDisease, diagnosisName, ... }`,
  // seçenek ise sistem tanısı `{ _id, name, code }`.
  const diagnosisKey = (opt) => opt?.diagnosisDisease ?? opt?._id;
  const diagnosisLabel = (opt) =>
    opt?.diagnosisDisease
      ? `${opt.diagnosisName || "Tanı"} (${opt.diagnosisCode || "-"})`
      : `${opt?.code || ""} ${opt?.name || ""}`.trim();

  const onDiagnosesChange = (next) => {
    setForm((p) => {
      const prev = p.items || [];
      if (next.length > prev.length) {
        const diagnosis = next[next.length - 1];
        if (!selectedCategory || !diagnosis) return p;
        return {
          ...p,
          items: [
            ...prev,
            {
              diagnosisCategory: selectedCategory._id,
              diagnosisCategoryName: selectedCategory.name,
              diagnosisDisease: diagnosis._id,
              diagnosisName: diagnosis.name,
              diagnosisCode: diagnosis.code || "",
            },
          ],
        };
      }
      return { ...p, items: next };
    });
  };

  const autoGenerate = async () => {
    if (diagnosisCategories.length === 0) return;
    const pick = (arr, n) =>
      [...arr].sort(() => Math.random() - 0.5).slice(0, Math.min(n, arr.length));
    const cats = pick(diagnosisCategories, 2);
    const generatedItems = [];
    for (const cat of cats) {
      try {
        const res = await listDiagnoses({
          token,
          categoryId: cat._id,
          status: "active",
          page: 1,
          limit: 200,
        }).unwrap();
        const diagnoses = res?.data?.diagnoses || [];
        const selected = pick(diagnoses, Math.floor(Math.random() * 3) + 1);
        for (const d of selected) {
          generatedItems.push({
            diagnosisCategory: cat._id,
            diagnosisCategoryName: cat.name,
            diagnosisDisease: d._id,
            diagnosisName: d.name,
            diagnosisCode: d.code || "",
          });
        }
      } catch (_) {
        /* ignore */
      }
    }
    setForm({
      name: `Test Hastalık Şablonu ${Math.floor(Math.random() * 900) + 100}`,
      items: generatedItems,
    });
  };

  const onSave = async () => {
    if (!token || !form.name.trim()) return;
    try {
      if (editing) {
        await updateDoctorDiagnosisTemplate({
          token,
          templateId: editing._id,
          name: form.name,
          items: form.items,
        }).unwrap();
        showSuccess("Şablon başarıyla güncellendi");
      } else {
        await createDoctorDiagnosisTemplate({
          token,
          name: form.name,
          items: form.items,
        }).unwrap();
        showSuccess("Şablon başarıyla oluşturuldu");
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Kaydetme işlemi başarısız.");
    }
  };

  const [togglingStatus, setTogglingStatus] = useState(false);
  const onToggleStatus = async (template) => {
    if (!token) return;
    setTogglingStatus(true);
    try {
      await setDoctorDiagnosisTemplateStatus({
        token,
        templateId: template._id,
        isActive: !template.isActive,
      }).unwrap();
      showSuccess(template.isActive ? "Şablon pasife alındı" : "Şablon aktifleştirildi");
      setEditing((prev) => (prev && prev._id === template._id ? { ...prev, isActive: !template.isActive } : prev));
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Durum güncellenemedi.");
    } finally {
      setTogglingStatus(false);
    }
  };

  const canSave = useMemo(() => form.name.trim().length > 2, [form.name]);

  const subText = { fontSize: TYPO.caption, color: COLORS.slate500 };
  const dash = <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>;
  const itemNames = (row) => (row.items || []).map((i) => i?.diagnosisName).filter(Boolean);
  const itemCodes = (row) => (row.items || []).map((i) => i?.diagnosisCode).filter(Boolean);

  return (
    <TemplateListPage
      icon={<FaNotesMedical size={20} />}
      title="Hastalık Şablonları"
      subtitle="Tanıları tek tıkla eklemek için şablonlar"
      query={query}
      onQueryChange={(v) => {
        setPage(0);
        setQuery(v);
      }}
      status={status}
      onStatusChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      onCreate={openCreate}
      errorMsg={errorMsg}
      isLoading={isLoading}
      columns={[
        { key: "name", label: "Şablon", width: "26%" },
        { key: "items", label: "Tanılar", width: "34%" },
        { key: "codes", label: "ICD kodları", width: "16%" },
        { key: "updated", label: "Güncellendi", width: "12%" },
        { key: "status", label: "Durum", width: "12%" },
      ]}
      rows={items}
      onRowClick={openEdit}
      renderRowCells={(row) => {
        const names = itemNames(row);
        const codes = itemCodes(row);
        const updated = row.updatedAt || row.createdAt;
        return [
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
            {row.name}
          </Typography>,
          names.length ? (
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} noWrap>
                {names.slice(0, 3).join(", ")}
              </Typography>
              <Typography sx={subText}>
                {names.length} tanı{names.length > 3 ? ` · +${names.length - 3} daha` : ""}
              </Typography>
            </Box>
          ) : (
            dash
          ),
          codes.length ? (
            <Typography
              sx={{ fontSize: TYPO.body, color: COLORS.slate600, fontVariantNumeric: "tabular-nums", pr: 2 }}
              title={codes.join(", ")}
              noWrap
            >
              {codes.slice(0, 3).join(", ")}
              {codes.length > 3 ? ` +${codes.length - 3}` : ""}
            </Typography>
          ) : (
            dash
          ),
          updated ? (
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} title={fullDateTime(updated)}>
              {relativePast(updated)}
            </Typography>
          ) : (
            dash
          ),
          <StatusPill label={row.isActive ? "Aktif" : "Pasif"} tone={row.isActive ? "success" : "neutral"} />,
        ];
      }}
      emptyIcon={<FaNotesMedical size={22} />}
      emptyMessage="Sık koyduğunuz tanı gruplarını şablon olarak kaydedin."
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    >
      <TemplateEditorDrawer
        open={open}
        onClose={() => setOpen(false)}
        icon={<FaNotesMedical size={18} />}
        title={editing ? "Şablonu düzenle" : "Yeni hastalık şablonu"}
        subtitle={editing ? editing.name : "Tanıları hızlıca eklemek için şablon oluşturun"}
        onSubmit={onSave}
        submitLabel={editing ? "Güncelle" : "Kaydet"}
        submitDisabled={!canSave}
        loading={isSaving}
        width={560}
        statusControl={
          editing
            ? { isActive: Boolean(editing.isActive), onToggle: () => onToggleStatus(editing), loading: togglingStatus }
            : null
        }
        headerExtra={
          !editing ? (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={autoGenerate}
              disabled={diagnosisCategories.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: COLORS.warning,
                color: COLORS.warningDark,
                "&:hover": { bgcolor: COLORS.warningLightest, borderColor: COLORS.warningDark },
              }}
            >
              Otomatik doldur (test)
            </Button>
          ) : null
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Şablon adı *" hint="En az 3 karakter.">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ör. Üst solunum yolu enfeksiyonları"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <DrawerField label="Tanı kategorisi" hint="Tanı eklemek için önce kategori seçin.">
            <FormSelect
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              InputProps={tileInputProps(true)}
              value={selectedCategory?._id || ""}
              onChange={(value) =>
                setSelectedCategory(diagnosisCategories.find((c) => c._id === value) || null)
              }
              options={diagnosisCategories}
              optionValue="_id"
              optionLabel="name"
              placeholder={null}
            />
          </DrawerField>
          <ChipListSelect
            addLabel="Tanı Ekle"
            label="Tanı Ekle"
            placeholder="Tanı"
            options={diagnosisOptions}
            value={form.items || []}
            onChange={onDiagnosesChange}
            getOptionId={diagnosisKey}
            getOptionLabel={diagnosisLabel}
            emptyText="Henüz tanı eklenmedi."
          />
        </Box>
      </TemplateEditorDrawer>
    </TemplateListPage>
  );
}
